import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  LabelList,
} from "recharts";
import {
  MoreVertical,
  TrendingUp,
  TrendingDown,
  ShoppingCart,
  Trash2,
  Edit3,
  Plus,
} from "lucide-react";
import { usePortfolioStore } from "../store/portfolioStore";
import { useThemeStore } from "../store/themeStore";
import { useBrokerStore } from "../store/brokerStore";
import { useTradingHistoryStore } from "../store/tradingHistoryStore";
import BrokerLogo from "./BrokerLogo";
import {
  formatCurrency,
  formatPercent,
  formatNumber,
  getStockTypeInfo,
  getSectorInfo,
  getCountryInfo,
} from "../lib/utils";
import { CHART_COLORS } from "../constants/chartConstants";
import AddTransactionModal from "./AddTransactionModal";
import TradingViewModal from "./TradingViewModal";
import StockLogo from "./StockLogo";
import PortfolioSummary from "./PortfolioSummary";

const PortfolioChart: React.FC = () => {
  const {
    getFilteredPortfolio,
    getCurrentPrice,
    convertPrice,
    selectedPortfolioId,
    getPortfolioCurrency,
    displayCurrency,
    openDeleteModal,
    sectors,
    countries,
    customPortfolios,
    portfolioHoldings,
    setSelectedPortfolio,
    loadPortfolioHoldings,
    loadPortfolio,
    loadingPrices,
  } = usePortfolioStore();
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [selectedSector, setSelectedSector] = useState<string | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"type" | "sector" | "country">("type");
  const [showIndividualStocks, setShowIndividualStocks] =
    useState<boolean>(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedHolding, setSelectedHolding] = useState<any>(null);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [buyHoldingTemplate, setBuyHoldingTemplate] = useState<any>(null);
  const [showSellModal, setShowSellModal] = useState(false);
  const [sellHoldingTemplate, setSellHoldingTemplate] = useState<any>(null);
  const [showTradingViewModal, setShowTradingViewModal] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState<string>("");
  const menuRef = useRef<HTMLDivElement>(null);

  const { maskAmounts } = useThemeStore();
  const MASK = "••••••";
  const m = (v: string) => (maskAmounts ? MASK : v);

  const { brokers, loadBrokers } = useBrokerStore();
  const { tradingHistory, loadTradingHistory } = useTradingHistoryStore();

  useEffect(() => {
    loadPortfolio();
    loadBrokers();
    loadTradingHistory();
  }, []);

  // Most recent broker used to trade a given symbol
  const getLatestBrokerKey = (symbol: string) => {
    return tradingHistory
      .filter((r) => r.symbol === symbol && r.broker)
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      )[0]?.broker;
  };

  const portfolio = getFilteredPortfolio();
  const portfolioCurrency = getPortfolioCurrency();
  const selectedPortfolio = selectedPortfolioId
    ? customPortfolios.find((p) => p.id === selectedPortfolioId)
    : null;

  // Function to find which portfolio a holding belongs to
  const findHoldingPortfolio = (holdingId: number) => {
    console.log("Finding portfolio for holding:", holdingId);
    console.log("Available portfolioHoldings:", portfolioHoldings);
    console.log("Available customPortfolios:", customPortfolios);

    for (const portfolioId in portfolioHoldings) {
      const holdings = portfolioHoldings[portfolioId] || [];
      console.log(`Checking portfolio ${portfolioId}:`, holdings);
      if (holdings.some((h) => h.id === holdingId)) {
        const foundPortfolio = customPortfolios.find(
          (p) => p.id === parseInt(portfolioId),
        );
        console.log("Found portfolio:", foundPortfolio);
        return foundPortfolio;
      }
    }
    console.log("No portfolio found for holding:", holdingId);
    return null;
  };

  // When a specific portfolio is selected, show individual stocks by default
  React.useEffect(() => {
    setShowIndividualStocks(!!selectedPortfolioId);
    if (selectedPortfolioId) {
      setSelectedType(null);
      setSelectedSector(null);
      setSelectedCountry(null);
    }
  }, [selectedPortfolioId]);

  // Load portfolio holdings for all portfolios when in All Portfolio view
  React.useEffect(() => {
    if (!selectedPortfolioId && customPortfolios.length > 0) {
      console.log("Loading holdings for all portfolios...");
      customPortfolios.forEach((portfolio) => {
        if (!portfolioHoldings[portfolio.id]) {
          console.log("Loading holdings for portfolio:", portfolio.name);
          loadPortfolioHoldings(portfolio.id);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedPortfolioId,
    customPortfolios.length, // Only track length to avoid re-renders when array reference changes
    // Removed: portfolioHoldings - causes infinite loop
    // Removed: loadPortfolioHoldings - stable function from store
  ]);

  // Handle clicks outside menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleOpenTradingView = (symbol: string, portApiType?: string) => {
    const tv = portApiType === 'th_stock' ? `SET:${symbol}` : symbol;
    setSelectedSymbol(tv);
    setShowTradingViewModal(true);
  };

  // Handle menu actions
  const handleMenuAction = (
    action: "chart" | "sell" | "delete" | "edit",
    holding: any,
  ) => {
    const portfolioHolding = portfolio.find((h) => h.symbol === holding.name);
    if (!portfolioHolding) return;

    switch (action) {
      case "chart":
        handleOpenTradingView(holding.name, portfolioHolding.port_api_type);
        break;
      case "edit":
        setSelectedHolding(portfolioHolding);
        setShowEditModal(true);
        break;
      case "sell": {
        const holdingPortfolio = findHoldingPortfolio(portfolioHolding.id);
        const sellCurrency = ['us_stock', 'us_fund'].includes(portfolioHolding.port_api_type) ? 'USD' : 'THB';
        setSellHoldingTemplate({
          symbol: portfolioHolding.symbol,
          name: portfolioHolding.name,
          portfolioId: holdingPortfolio?.id?.toString() || '',
          port_api_type: portfolioHolding.port_api_type,
          sector: portfolioHolding.sector,
          country: portfolioHolding.country,
          currency: sellCurrency as 'USD' | 'THB',
          action: 'SELL' as const,
          maxShares: portfolioHolding.shares,
          currentPrice: getCurrentPrice(portfolioHolding.symbol, sellCurrency),
          purchasePrice: portfolioHolding.purchase_price,
          purchaseCurrency: portfolioHolding.purchase_currency,
        });
        setShowSellModal(true);
        break;
      }
      case "delete":
        openDeleteModal(portfolioHolding);
        break;
    }
    setOpenMenuId(null);
  };

  if (portfolio.length === 0) return null;

  // Group by portfolio when in All Portfolio view, otherwise by stock type
  const typeGroups = portfolio.reduce((groups, holding) => {
    const currentPrice = getCurrentPrice(holding.symbol, portfolioCurrency);
    const value = currentPrice * holding.shares;

    if (value <= 0) return groups;

    // Calculate invested amount and gain
    const purchasePriceInDisplayCurrency = convertPrice(
      holding.purchase_price,
      holding.purchase_currency,
      portfolioCurrency,
    );
    const invested = purchasePriceInDisplayCurrency * holding.shares;
    const gain = value - invested;

    // Determine grouping key and info
    let groupKey: string;
    let groupInfo: any;

    if (!selectedPortfolioId) {
      // In All Portfolio view, group by portfolio
      console.log("Processing holding in All Portfolio view:", holding);
      const holdingPortfolio = findHoldingPortfolio(holding.id);
      if (holdingPortfolio) {
        console.log("Grouping by portfolio:", holdingPortfolio.name);
        groupKey = `portfolio_${holdingPortfolio.id}`;
        groupInfo = {
          type: `portfolio_${holdingPortfolio.id}`,
          typeLabel: holdingPortfolio.name,
          flag: holdingPortfolio.icon || "📊",
          portfolioId: holdingPortfolio.id,
        };
      } else {
        // Fallback to stock type if not in any portfolio
        console.log("Fallback to stock type for:", holding.symbol);
        const typeInfo = getStockTypeInfo(holding.port_api_type);
        groupKey = holding.port_api_type;
        groupInfo = {
          type: holding.port_api_type,
          typeLabel: typeInfo.labelTh,
          flag: typeInfo.flag,
        };
      }
    } else {
      // In specific portfolio view, group by stock type
      const typeInfo = getStockTypeInfo(holding.port_api_type);
      groupKey = holding.port_api_type;
      groupInfo = {
        type: holding.port_api_type,
        typeLabel: typeInfo.labelTh,
        flag: typeInfo.flag,
      };
    }

    if (!groups[groupKey]) {
      groups[groupKey] = {
        ...groupInfo,
        totalValue: 0,
        totalInvested: 0,
        totalGain: 0,
        holdings: [],
      };
    }

    groups[groupKey].totalValue += value;
    groups[groupKey].totalInvested += invested;
    groups[groupKey].totalGain += gain;
    groups[groupKey].holdings.push({
      id: holding.id,
      name: holding.symbol,
      value: value,
      invested: invested,
      gain: gain,
      shares: holding.shares,
      port_api_type: holding.port_api_type,
      typeLabel: groupInfo.typeLabel,
      flag: groupInfo.flag,
      sector: holding.sector,
      percentage: 0,
    });

    return groups;
  }, {} as any);

  // Group by sector
  const sectorGroups = portfolio.reduce((groups, holding) => {
    const currentPrice = getCurrentPrice(holding.symbol, portfolioCurrency);
    const value = currentPrice * holding.shares;

    if (value <= 0) return groups;

    const sector = holding.sector || "other";
    const sectorInfo = getSectorInfo(sector, sectors);

    // Calculate invested amount and gain
    const purchasePriceInDisplayCurrency = convertPrice(
      holding.purchase_price,
      holding.purchase_currency,
      portfolioCurrency,
    );
    const invested = purchasePriceInDisplayCurrency * holding.shares;
    const gain = value - invested;

    if (!groups[sector]) {
      groups[sector] = {
        sector: sector,
        sectorLabel: sectorInfo.label,
        icon: sectorInfo.icon,
        totalValue: 0,
        totalInvested: 0,
        totalGain: 0,
        holdings: [],
      };
    }

    groups[sector].totalValue += value;
    groups[sector].totalInvested += invested;
    groups[sector].totalGain += gain;
    groups[sector].holdings.push({
      id: holding.id,
      name: holding.symbol,
      value: value,
      invested: invested,
      gain: gain,
      shares: holding.shares,
      port_api_type: holding.port_api_type,
      sector: sector,
      sectorLabel: sectorInfo.label,
      icon: sectorInfo.icon,
      percentage: 0,
    });

    return groups;
  }, {} as any);

  // Group by country
  const countryGroups = portfolio.reduce((groups, holding) => {
    const currentPrice = getCurrentPrice(holding.symbol, portfolioCurrency);
    const value = currentPrice * holding.shares;

    if (value <= 0) return groups;

    const avgCost = convertPrice(holding.purchase_price, holding.purchase_currency, portfolioCurrency);
    const invested = avgCost * holding.shares;
    const gain = value - invested;

    // Use actual country field from holding, with fallback to hardcoded mapping
    let countryKey: string;
    let countryInfo: any;

    if (holding.country) {
      // Use actual country from holding
      const countryFromDB = countries.find(c => c.key === holding.country);
      if (countryFromDB) {
        countryKey = holding.country;
        countryInfo = {
          country: countryKey,
          label: countryFromDB.label,
          flag: countryFromDB.icon,
          color: countryFromDB.color,
          bgColor: countryFromDB.bg_color,
          textColor: countryFromDB.text_color,
          borderColor: countryFromDB.border_color
        };
      } else {
        // Fallback to hardcoded mapping if country not found in DB
        const fallbackInfo = getCountryInfo(holding.port_api_type);
        countryKey = fallbackInfo.country;
        countryInfo = fallbackInfo;
      }
    } else {
      // Fallback to hardcoded mapping if no country field
      const fallbackInfo = getCountryInfo(holding.port_api_type);
      countryKey = fallbackInfo.country;
      countryInfo = fallbackInfo;
    }

    if (!groups[countryKey]) {
      groups[countryKey] = {
        country: countryKey,
        countryLabel: countryInfo.label,
        flag: countryInfo.flag,
        totalValue: 0,
        totalInvested: 0,
        totalGain: 0,
        holdings: [],
        percentage: 0,
      };
    }

    groups[countryKey].holdings.push({
      ...holding,
      value,
      currentPrice,
      avgCost,
      invested,
      gain,
      percentage: 0, // Will be calculated later
    });

    groups[countryKey].totalValue += value;
    groups[countryKey].totalInvested += invested;
    groups[countryKey].totalGain += gain;

    return groups;
  }, {} as any);

  // Calculate total portfolio value
  const totalValue = Object.values(typeGroups).reduce(
    (sum: number, group: any) => sum + group.totalValue,
    0,
  );

  // Prepare chart data based on selected view
  let chartData: any[];

  if (showIndividualStocks && selectedPortfolioId) {
    // Show individual stocks when a specific portfolio is selected
    chartData = portfolio
      .map((holding) => {
        const currentPrice = getCurrentPrice(holding.symbol, portfolioCurrency);
        const value = currentPrice * holding.shares;
        const purchasePriceInDisplayCurrency = convertPrice(
          holding.purchase_price,
          holding.purchase_currency,
          portfolioCurrency,
        );
        const invested = purchasePriceInDisplayCurrency * holding.shares;
        const gain = value - invested;

        return {
          id: holding.id,
          name: holding.symbol,
          value: value,
          invested: invested,
          gain: gain,
          shares: holding.shares,
          port_api_type: holding.port_api_type,
          sector: holding.sector,
          percentage: (value / totalValue) * 100,
        };
      })
      .filter((item) => item.value > 0)
      .sort((a, b) => b.percentage - a.percentage); // Sort by allocation percentage (highest first)
  } else if (selectedType && typeGroups[selectedType]) {
    // Show individual stocks in selected type
    chartData = typeGroups[selectedType].holdings;
    const typeTotal = typeGroups[selectedType].totalValue;
    chartData.forEach((item: any) => {
      item.percentage = (item.value / typeTotal) * 100;
    });
    // Sort by allocation percentage (highest first)
    chartData.sort((a: any, b: any) => b.percentage - a.percentage);
  } else if (selectedSector && sectorGroups[selectedSector]) {
    // Show individual stocks in selected sector
    chartData = sectorGroups[selectedSector].holdings;
    const sectorTotal = sectorGroups[selectedSector].totalValue;
    chartData.forEach((item: any) => {
      item.percentage = (item.value / sectorTotal) * 100;
    });
    // Sort by allocation percentage (highest first)
    chartData.sort((a: any, b: any) => b.percentage - a.percentage);
  } else if (selectedCountry && countryGroups[selectedCountry]) {
    // Show individual stocks in selected country
    chartData = countryGroups[selectedCountry].holdings;
    const countryTotal = countryGroups[selectedCountry].totalValue;
    chartData.forEach((item: any) => {
      item.percentage = (item.value / countryTotal) * 100;
    });
    // Sort by allocation percentage (highest first)
    chartData.sort((a: any, b: any) => b.percentage - a.percentage);
  } else if (viewMode === "country") {
    // Show countries
    chartData = Object.values(countryGroups).map((group: any) => ({
      name: group.countryLabel,
      country: group.country,
      value: group.totalValue,
      flag: group.flag,
      holdingsCount: group.holdings.length,
      percentage: (group.totalValue / totalValue) * 100,
    }));
    // Sort by allocation percentage (highest first)
    chartData.sort((a: any, b: any) => b.percentage - a.percentage);
  } else if (viewMode === "sector") {
    // Show sectors
    chartData = Object.values(sectorGroups).map((group: any) => ({
      name: group.sectorLabel,
      sector: group.sector,
      value: group.totalValue,
      flag: group.icon,
      holdingsCount: group.holdings.length,
      percentage: (group.totalValue / totalValue) * 100,
    }));
    // Sort by allocation percentage (highest first)
    chartData.sort((a: any, b: any) => b.percentage - a.percentage);
  } else {
    // Show stock types
    chartData = Object.values(typeGroups).map((group: any) => ({
      name: group.typeLabel,
      type: group.type,
      value: group.totalValue,
      flag: group.flag,
      holdingsCount: group.holdings.length,
      percentage: (group.totalValue / totalValue) * 100,
      portfolioId: group.portfolioId, // Add portfolioId for portfolio groups
    }));
    // Sort by allocation percentage (highest first)
    chartData.sort((a: any, b: any) => b.percentage - a.percentage);
  }

  // Colors for the chart
  const COLORS = CHART_COLORS;

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">{data.flag}</span>
            <span className="font-semibold">{data.name}</span>
          </div>
          <div className="space-y-1 text-sm">
            {selectedType || selectedSector || selectedCountry || showIndividualStocks ? (
              <>
                <p>
                  Shares:{" "}
                  {data.shares
                    ? formatNumber(data.shares, { maximumFractionDigits: 4 })
                    : "0"}
                </p>
                <p>Value: {m(formatCurrency(convertPrice(data.value, portfolioCurrency, displayCurrency), displayCurrency))}</p>
                <p>Allocation: {formatPercent(data.percentage)}</p>
              </>
            ) : (
              <>
                <p>Holdings: {data.holdingsCount} stocks</p>
                <p>Value: {m(formatCurrency(convertPrice(data.value, portfolioCurrency, displayCurrency), displayCurrency))}</p>
                <p>Allocation: {formatPercent(data.percentage)}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Click to view details
                </p>
              </>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      {/* Portfolio Allocation Chart with integrated Summary */}
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: 'spring', stiffness: 320, damping: 26, mass: 0.7 }}
        className="card p-6  min-h-fit"
      >
        {/* Portfolio Summary Cards - Inside white container */}
        <div className="mb-8">
          <PortfolioSummary />
        </div>

        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            {(selectedType || selectedSector || selectedCountry || selectedPortfolioId) && (
              <button
                onClick={() => {
                  if (selectedPortfolioId) {
                    setSelectedPortfolio(null);
                  } else {
                    setSelectedType(null);
                    setSelectedSector(null);
                    setSelectedCountry(null);
                  }
                }}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title="Back to overview"
              >
                <svg
                  className="w-5 h-5 text-gray-600 dark:text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
            )}
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {showIndividualStocks && selectedPortfolioId
                ? `${selectedPortfolio?.icon || "📊"} ${selectedPortfolio?.name || "Portfolio"} Holdings`
                : selectedType
                  ? `${typeGroups[selectedType]?.flag} ${typeGroups[selectedType]?.typeLabel}`
                  : selectedSector
                    ? `${sectorGroups[selectedSector]?.icon} ${sectorGroups[selectedSector]?.sectorLabel}`
                    : selectedCountry
                      ? `${countryGroups[selectedCountry]?.flag} ${countryGroups[selectedCountry]?.countryLabel}`
                      : "Portfolio Allocation"}
            </h2>
          </div>
          <div className="flex items-center gap-4">
            {!selectedType && !selectedSector && !selectedCountry && !showIndividualStocks && (
              <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button
                  onClick={() => {
                    setViewMode("type");
                    setSelectedType(null);
                    setSelectedSector(null);
                    setSelectedCountry(null);
                  }}
                  className={`inline-flex items-center justify-center px-3 h-7 text-xs rounded-md transition-colors ${
                    viewMode === "type"
                      ? "bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm"
                      : "text-gray-600 dark:text-gray-400"
                  }`}
                >
                  By Port
                </button>
                <button
                  onClick={() => {
                    setViewMode("sector");
                    setSelectedType(null);
                    setSelectedSector(null);
                    setSelectedCountry(null);
                  }}
                  className={`inline-flex items-center justify-center px-3 h-7 text-xs rounded-md transition-colors ${
                    viewMode === "sector"
                      ? "bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm"
                      : "text-gray-600 dark:text-gray-400"
                  }`}
                >
                  By Sector
                </button>
                <button
                  onClick={() => {
                    setViewMode("country");
                    setSelectedType(null);
                    setSelectedSector(null);
                    setSelectedCountry(null);
                  }}
                  className={`inline-flex items-center justify-center px-3 h-7 text-xs rounded-md transition-colors ${
                    viewMode === "country"
                      ? "bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm"
                      : "text-gray-600 dark:text-gray-400"
                  }`}
                >
                  By Country
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-10 gap-6">
          {/* Pie Chart */}
          <div className="h-80 lg:col-span-2">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={0}
                  outerRadius="100%"
                  paddingAngle={0}
                  dataKey="value"
                  onClick={(data) => {
                    if (
                      !selectedType &&
                      !selectedSector &&
                      !showIndividualStocks &&
                      viewMode === "type" &&
                      data.type
                    ) {
                      // Check if it's a portfolio group
                      if (
                        data.type.startsWith("portfolio_") &&
                        data.portfolioId
                      ) {
                        setSelectedPortfolio(data.portfolioId);
                      } else {
                        setSelectedType(data.type);
                      }
                    } else if (
                      !selectedType &&
                      !selectedSector &&
                      !showIndividualStocks &&
                      viewMode === "sector" &&
                      data.sector
                    ) {
                      setSelectedSector(data.sector);
                    } else if (
                      !selectedType &&
                      !selectedSector &&
                      !selectedCountry &&
                      !showIndividualStocks &&
                      viewMode === "country" &&
                      data.country
                    ) {
                      setSelectedCountry(data.country);
                    }
                  }}
                  style={{
                    cursor:
                      selectedType || selectedSector || selectedCountry || showIndividualStocks
                        ? "default"
                        : "pointer",
                  }}
                >
                  {chartData.map((_, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                      stroke="none"
                      strokeWidth={0}
                    />
                  ))}
                  <LabelList
                    dataKey={(entry) => `${entry.name} (${entry.percentage.toFixed(1)}%)`}
                    position="inside"
                    fill="#fff"
                    fontSize={9}
                    fontWeight="bold"
                    textAnchor="middle"
                    style={{ textShadow: "none" }}
                  />
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Legend */}
          <div className="space-y-3 lg:col-span-8">
            {/* Table Header */}
            <div
              className={`grid ${selectedType || selectedSector || selectedCountry || showIndividualStocks ? "grid-cols-[repeat(14,minmax(0,1fr))]" : "grid-cols-[repeat(13,minmax(0,1fr))]"} gap-2 px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-600`}
            >
              <div className="col-span-5">Name</div>
              <div className="col-span-2 text-right">
                {selectedType || selectedSector || selectedCountry || showIndividualStocks
                  ? "Current / Avg Price"
                  : ""}
              </div>
              <div className="col-span-2 text-right">Value / Invested</div>
              <div className="col-span-2 text-right">Unrealized P&L</div>
              <div className="col-span-2 text-right">Allocation</div>
              {(selectedType || selectedSector || selectedCountry || showIndividualStocks) && (
                <div className="col-span-1 text-center"></div>
              )}
            </div>

            <div
              className="space-y-2 dropdown-menu-container"
              style={{ overflowX: "visible" }}
            >
              {loadingPrices && chartData.length === 0 ? (
                [70, 55, 80, 65, 75].map((w, i) => (
                  <div key={i} className="grid grid-cols-[repeat(13,minmax(0,1fr))] gap-2 items-center p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                    <div className="col-span-5 flex items-center gap-3">
                      <div className="w-0.5 h-4 rounded-sm bg-gray-200 dark:bg-gray-600" />
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" style={{ width: `${w}px` }} />
                    </div>
                    <div className="col-span-2 flex justify-end"><div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" /></div>
                    <div className="col-span-2 flex justify-end"><div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" /></div>
                    <div className="col-span-2 flex justify-end"><div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" /></div>
                    <div className="col-span-2 flex justify-end"><div className="h-4 w-12 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" /></div>
                  </div>
                ))
              ) : null}
              {chartData.map((item, index) => {
                // Get invested and gain values
                let invested = 0,
                  gain = 0;

                if (selectedType || selectedSector || selectedCountry || showIndividualStocks) {
                  invested = item.invested || 0;
                  gain = item.gain || 0;
                } else if (viewMode === "type" && typeGroups[item.type]) {
                  invested = typeGroups[item.type].totalInvested;
                  gain = typeGroups[item.type].totalGain;
                } else if (viewMode === "sector" && sectorGroups[item.sector]) {
                  invested = sectorGroups[item.sector].totalInvested;
                  gain = sectorGroups[item.sector].totalGain;
                } else if (viewMode === "country" && countryGroups[item.country]) {
                  invested = countryGroups[item.country].totalInvested;
                  gain = countryGroups[item.country].totalGain;
                }

                return (
                  <div
                    key={item.id ?? (item.type || item.sector || item.country || item.name)}
                    onClick={() => {
                      if (
                        !selectedType &&
                        !selectedSector &&
                        !showIndividualStocks &&
                        viewMode === "type" &&
                        item.type
                      ) {
                        // Check if it's a portfolio group
                        if (
                          item.type.startsWith("portfolio_") &&
                          item.portfolioId
                        ) {
                          setSelectedPortfolio(item.portfolioId);
                        } else {
                          setSelectedType(item.type);
                        }
                      } else if (
                        !selectedType &&
                        !selectedSector &&
                        !showIndividualStocks &&
                        viewMode === "sector" &&
                        item.sector
                      ) {
                        setSelectedSector(item.sector);
                      } else if (
                        !selectedType &&
                        !selectedSector &&
                        !selectedCountry &&
                        !showIndividualStocks &&
                        viewMode === "country" &&
                        item.country
                      ) {
                        setSelectedCountry(item.country);
                      }
                    }}
                    className={`grid ${selectedType || selectedSector || selectedCountry || showIndividualStocks ? "grid-cols-[repeat(14,minmax(0,1fr))]" : "grid-cols-[repeat(13,minmax(0,1fr))]"} gap-2 items-center p-3 rounded-lg transition-colors ${
                      selectedType || selectedSector || selectedCountry || showIndividualStocks
                        ? "bg-gray-50 dark:bg-gray-700/50"
                        : "bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                    }`}
                  >
                    {/* Name Column */}
                    <div className="col-span-5 flex items-center gap-3">
                      <div
                        className="w-0.5 h-4 flex-shrink-0 rounded-sm"
                        style={{
                          backgroundColor: COLORS[index % COLORS.length],
                        }}
                      />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          {selectedType ||
                          selectedSector ||
                          selectedCountry ||
                          showIndividualStocks ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenTradingView(item.name, item.port_api_type);
                              }}
                              title="ดูกราฟ"
                              className="flex-shrink-0"
                            >
                              <StockLogo
                                symbol={item.name}
                                size={20}
                                currency={portfolioCurrency}
                              />
                            </button>
                          ) : (
                            <span className="text-xs">{item.flag}</span>
                          )}
                          <div className="flex flex-col">
                            <span
                              className={`text-sm font-medium text-gray-900 dark:text-white truncate ${
                                selectedType ||
                                selectedSector ||
                                showIndividualStocks
                                  ? "cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                  : ""
                              }`}
                              onClick={(e) => {
                                if (
                                  selectedType ||
                                  selectedSector ||
                                  selectedCountry ||
                                  showIndividualStocks
                                ) {
                                  e.stopPropagation();
                                  handleOpenTradingView(item.name, item.port_api_type);
                                }
                              }}
                              title={
                                selectedType ||
                                selectedSector ||
                                showIndividualStocks
                                  ? "Click to view chart"
                                  : ""
                              }
                            >
                              {item.name}
                            </span>
                            {/* Sector Label */}
                            {(selectedType ||
                              selectedSector ||
                              showIndividualStocks) &&
                              (() => {
                                const holding = portfolio.find(
                                  (h) => h.symbol === item.name,
                                );

                                return (
                                  <div className="flex items-center gap-1 flex-wrap">
                                    {holding && holding.country && (
                                      (() => {
                                        const countryInfo = countries.find(c => c.key === holding.country);
                                        if (!countryInfo) return null;
                                        return (
                                          <div
                                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full font-medium ${countryInfo.bg_color || "bg-gray-100 dark:bg-gray-700"} ${countryInfo.text_color || "text-gray-700 dark:text-gray-300"}`}
                                          >
                                            <span style={{ fontSize: "8px" }}>
                                              {countryInfo.icon}
                                            </span>
                                            <span
                                              className="truncate"
                                              style={{ fontSize: "8px" }}
                                            >
                                              {countryInfo.label}
                                            </span>
                                          </div>
                                        );
                                      })()
                                    )}

                                    {holding && holding.sector && (
                                      (() => {
                                        const sectorInfo = getSectorInfo(
                                          holding.sector,
                                          sectors,
                                        );
                                        return (
                                          <div
                                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full font-medium ${sectorInfo.bgColor || "bg-gray-100 dark:bg-gray-700"} ${sectorInfo.textColor || "text-gray-700 dark:text-gray-300"}`}
                                          >
                                            <span style={{ fontSize: "8px" }}>
                                              {sectorInfo.icon}
                                            </span>
                                            <span
                                              className="truncate"
                                              style={{ fontSize: "8px" }}
                                            >
                                              {sectorInfo.label}
                                            </span>
                                          </div>
                                        );
                                      })()
                                    )}

                                    {holding && (() => {
                                      const brokerKey = holding.broker || getLatestBrokerKey(holding.symbol);
                                      if (!brokerKey) return null;
                                      const brokerInfo = brokers.find((b) => b.key === brokerKey);
                                      if (!brokerInfo) return null;
                                      return (
                                        <div
                                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full font-medium ${brokerInfo.bg_color || "bg-gray-100 dark:bg-gray-700"} ${brokerInfo.text_color || "text-gray-700 dark:text-gray-300"}`}
                                        >
                                          <BrokerLogo
                                            brokerKey={brokerInfo.key}
                                            fallbackIcon={brokerInfo.icon}
                                            size={14}
                                          />
                                          <span
                                            className="truncate"
                                            style={{ fontSize: "8px" }}
                                          >
                                            {brokerInfo.label}
                                          </span>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                );
                              })()}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Combined Current/Avg Price Column */}
                    <div className="col-span-2 text-right text-xs">
                      {selectedType ||
                      selectedSector ||
                      showIndividualStocks ? (
                        <div>
                          {(() => {
                            const holding = portfolio.find(
                              (h) => h.symbol === item.name,
                            );
                            if (holding) {
                              const currentPrice = getCurrentPrice(
                                holding.symbol,
                                displayCurrency,
                              );
                              const avgCost = convertPrice(
                                holding.purchase_price,
                                holding.purchase_currency,
                                displayCurrency,
                              );
                              return (
                                <>
                                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                                    {m(formatCurrency(currentPrice, displayCurrency))}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {m(formatCurrency(avgCost, displayCurrency))}
                                  </div>
                                </>
                              );
                            }
                            return "-";
                          })()}
                        </div>
                      ) : (
                        <div className="text-gray-500 dark:text-gray-400 text-xs"></div>
                      )}
                    </div>

                    {/* Value/Invested Column */}
                    <div className="col-span-2 text-right text-xs">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {m(formatCurrency(convertPrice(item.value, portfolioCurrency, displayCurrency), displayCurrency))}
                      </div>
                      <div className="text-gray-500 dark:text-gray-400">
                        {m(formatCurrency(convertPrice(invested, portfolioCurrency, displayCurrency), displayCurrency))}
                      </div>
                    </div>

                    {/* Gain Column */}
                    <div className="col-span-2 text-right text-xs">
                      <div
                        className={`font-semibold text-sm ${gain >= 0 ? "text-gain" : "text-loss"}`}
                      >
                        {m(`${gain >= 0 ? "+" : ""}${formatCurrency(convertPrice(gain, portfolioCurrency, displayCurrency), displayCurrency)}`)}
                      </div>
                      <div className="flex justify-end mt-0.5">
                        <span
                          className={`inline-flex items-center gap-0.5 text-xs font-semibold px-2 py-0.5 rounded-md tabular-nums ${gain >= 0 ? "bg-gradient-to-r from-green-50 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 text-gain" : "bg-gradient-to-r from-rose-50 to-pink-100 dark:from-rose-900/30 dark:to-pink-900/30 text-loss"}`}
                        >
                          {gain >= 0 ? (
                            <TrendingUp className="w-3 h-3" />
                          ) : (
                            <TrendingDown className="w-3 h-3" />
                          )}
                          {invested > 0
                            ? `${gain >= 0 ? "+" : ""}${formatPercent((gain / invested) * 100).replace("%", "")}%`
                            : "0.00%"}
                        </span>
                      </div>
                    </div>

                    {/* Allocation Column */}
                    <div className="col-span-2 text-right text-xs">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {formatPercent(item.percentage)}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {selectedType || selectedSector || selectedCountry || showIndividualStocks
                          ? `${item.shares ? formatNumber(item.shares, { maximumFractionDigits: 2 }) : "0"} shares`
                          : `${item.holdingsCount} holdings`}
                      </p>
                    </div>

                    {/* Actions Menu (only for individual stocks) */}
                    {(selectedType ||
                      selectedSector ||
                      showIndividualStocks) && (
                      <div className="col-span-1 flex justify-center">
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenMenuId(
                                openMenuId === item.name ? null : item.name,
                              );
                            }}
                            className="p-1 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                            title="More options"
                          >
                            <MoreVertical className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          </button>

                          {/* Dropdown Menu */}
                          {openMenuId === item.name && (
                            <motion.div
                              initial={{ opacity: 0, scale: 0.95 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.95 }}
                              className={`absolute ${index < 2 ? "top-full mt-1" : "bottom-full mb-1"} right-0 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 min-w-36 z-[60]`}
                              style={{
                                boxShadow:
                                  "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                              }}
                            >
                              <div className="py-1">
                                <button
                                  onClick={() =>
                                    handleMenuAction("chart", item)
                                  }
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                >
                                  <TrendingUp className="w-4 h-4" />
                                  Chart
                                </button>
                                <button
                                  onClick={() => {
                                    const portfolioHolding = portfolio.find((h) => h.symbol === item.name);
                                    if (portfolioHolding) {
                                      // Find which portfolio this holding belongs to
                                      const holdingPortfolio = findHoldingPortfolio(portfolioHolding.id);

                                      // Create a template with only basic info, excluding price and shares
                                      setBuyHoldingTemplate({
                                        symbol: portfolioHolding.symbol,
                                        name: portfolioHolding.name,
                                        port_api_type: portfolioHolding.port_api_type,
                                        sector: portfolioHolding.sector,
                                        country: portfolioHolding.country,
                                        purchase_currency: portfolioHolding.purchase_currency,
                                        portfolioId: holdingPortfolio?.id?.toString() || '',
                                        // Explicitly exclude: shares, purchase_price, id
                                      });
                                      setShowBuyModal(true);
                                    }
                                    setOpenMenuId(null);
                                  }}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                >
                                  <Plus className="w-4 h-4" />
                                  Buy
                                </button>
                                <button
                                  onClick={() => handleMenuAction("sell", item)}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                >
                                  <ShoppingCart className="w-4 h-4" />
                                  Sell
                                </button>
                                <button
                                  onClick={() => handleMenuAction("edit", item)}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                >
                                  <Edit3 className="w-4 h-4" />
                                  Edit
                                </button>
                                <button
                                  onClick={() =>
                                    handleMenuAction("delete", item)
                                  }
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-loss hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  Delete
                                </button>
                              </div>
                            </motion.div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </motion.div>

      <AddTransactionModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedHolding(null);
        }}
        editingHolding={selectedHolding}
      />

      <AddTransactionModal
        isOpen={showBuyModal}
        onClose={() => {
          setShowBuyModal(false);
          setBuyHoldingTemplate(null);
        }}
        prefillData={buyHoldingTemplate ? {
          symbol: buyHoldingTemplate.symbol,
          portfolioId: buyHoldingTemplate.portfolioId,
          port_api_type: buyHoldingTemplate.port_api_type,
          sector: buyHoldingTemplate.sector,
          country: buyHoldingTemplate.country,
          currency: buyHoldingTemplate.purchase_currency,
          action: 'BUY',
        } : undefined}
      />

      <AddTransactionModal
        isOpen={showSellModal}
        onClose={() => {
          setShowSellModal(false);
          setSellHoldingTemplate(null);
        }}
        prefillData={sellHoldingTemplate ?? undefined}
      />

      <TradingViewModal
        isOpen={showTradingViewModal}
        onClose={() => {
          setShowTradingViewModal(false);
          setSelectedSymbol("");
        }}
        symbol={selectedSymbol}
      />
    </>
  );
};

export default PortfolioChart;
